import { useState, useRef, useCallback } from 'react';
import { Platform } from 'react-native';
import { v4 as uuidv4 } from 'uuid';
import { logError, logInfo } from '../../utils/logger';

const PLACES_BASE_URL = 'https://places.googleapis.com';

export default function useGooglePlacesAutocomplete({ apiKey, proxyUrl, debounceMs = 400 }) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const sessionTokenRef = useRef(uuidv4());

  const isWeb = Platform.OS === 'web';

  const getBaseUrl = useCallback(() => (isWeb ? proxyUrl : PLACES_BASE_URL), [isWeb, proxyUrl]);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = uuidv4();
  }, []);

  const fetchSuggestions = useCallback(
    (input) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();

      if (!input || input.trim().length < 2) {
        setSuggestions([]);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortRef.current = controller;
        setIsLoading(true);

        try {
          const baseUrl = getBaseUrl();
          const headers = { 'Content-Type': 'application/json' };
          if (!isWeb) headers['X-Goog-Api-Key'] = apiKey;
          const res = await fetch(`${baseUrl}/v1/places:autocomplete`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              input: input.trim(),
              languageCode: 'en',
              includedRegionCodes: ['us'],
              sessionToken: sessionTokenRef.current,
            }),
            signal: controller.signal,
          });

          const data = await res.json();
          const items = (data.suggestions || [])
            .filter((s) => s.placePrediction)
            .map((s) => ({
              placeId: s.placePrediction.placeId,
              description:
                s.placePrediction.text?.text || s.placePrediction.structuredFormat?.mainText?.text || '',
            }));
          setSuggestions(items);
        } catch (err) {
          if (err.name !== 'AbortError') {
            // Web proxy doesn't support new Places API yet — log as info to avoid Sentry noise
            const log = isWeb ? logInfo : logError;
            log(isWeb ? 'Places autocomplete fetch failed (web proxy not yet updated)' : err, {
              context: 'Places autocomplete fetch',
            });
            setSuggestions([]);
          }
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);
    },
    [apiKey, debounceMs, getBaseUrl, isWeb]
  );

  const fetchPlaceDetails = useCallback(
    async (placeId) => {
      if (abortRef.current) abortRef.current.abort();

      try {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/v1/places/${placeId}?sessionToken=${encodeURIComponent(
          sessionTokenRef.current
        )}`;
        const headers = { 'Content-Type': 'application/json', 'X-Goog-FieldMask': 'id,addressComponents' };
        if (!isWeb) headers['X-Goog-Api-Key'] = apiKey;
        const res = await fetch(url, {
          method: 'GET',
          headers,
        });

        const details = await res.json();
        resetSessionToken();
        return details;
      } catch (err) {
        if (err.name !== 'AbortError') {
          const log = isWeb ? logInfo : logError;
          log(isWeb ? 'Places details fetch failed (web proxy not yet updated)' : err, {
            context: 'Places details fetch',
          });
        }
        return null;
      }
    },
    [apiKey, getBaseUrl, isWeb, resetSessionToken]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return { suggestions, fetchSuggestions, fetchPlaceDetails, clearSuggestions, isLoading };
}
